import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, LayoutTemplate, Loader2, Save } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Button from '../components/ui/Button';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import ErrorDialog from '../components/ui/ErrorDialog';
import { FunctionalSelect, SelectItem } from '../components/ui/Select';
import { HEADER_SELECT_CLASS, PageHeaderActions } from '../components/PageHeaderActions';
import CriteriaTree from '../components/segmentBuilder/CriteriaTree';
import SegmentSummaryPane from '../components/segmentBuilder/SegmentSummaryPane';
import TemplatePicker from '../components/segmentBuilder/TemplatePicker';
import NlDraftBar from '../components/segmentBuilder/NlDraftBar';
import SaveStatus from '../components/segmentBuilder/SaveStatus';
import SaveTemplateDialog from '../components/segmentBuilder/SaveTemplateDialog';
import { canManageTemplates, segmentTemplatesService } from '../services/segmentTemplatesService';
import { useAuth } from '../contexts/AuthContext';
import usePermissions from '../hooks/usePermissions';
import { PERMISSIONS } from '../services/permissions';
import {
  anyGroupSampled,
  contradictionIssues,
  criteriaIncompleteReason,
  criteriaIsComplete,
  defaultCriteriaRoot,
  fitToTarget,
  hoistSampleToAncestor,
  mergeSameColumnAtPath,
  pathKey,
  samplingShapeIssues,
  setOpAtPath,
} from '../components/segmentBuilder/criteriaUtils';
import {
  collectConditions,
  criteriaFromSegment,
  formatApiErrorDetail,
  hydrateCriteria,
  sanitizeName,
} from '../components/segmentBuilder/criteriaEditorHelpers';
import { audienceRuleCount, decodeRules } from '../services/offerStudioService';
import { buildCriteriaFromOffer, buildCriteriaFromVertical, GENERAL_TEMPLATE_VALUE } from '../components/segmentBuilder/verticalTemplate';
import { criteriaFromOfferSeed } from '../components/segmentBuilder/offerValueRules';
import OfferSearchTypeahead, { offerLabel } from '../components/OfferSearchTypeahead';
import { api } from '../services/authService';
import { FASTCUBE_ORG, organizationsService } from '../services/organizationsService';
import { wakeSqlWarehouse } from '../services/sqlWarehouseService';
import { buildFileDownloadHref } from '../services/fileDownload';
import { SEGMENTS_LIST_PAGE_KEY, listReturnPath } from '../utils/listPage';
import { ORG_LOCAL_SUFFIX, readOrgFlag, writeOrgLocal } from '../utils/orgLocalStorage';
import '../components/segmentBuilder/CriteriaBuilder.css';

const PREVIEW_DEBOUNCE_MS = 450;
const COUNT_DEBOUNCE_MS = 1500;
const SAVE_DEBOUNCE_MS = 2500;
const RUN_POLL_MS = 15000;

function schedulePayload(schedule, dow, dom) {
  if (!schedule) return { schedule: null, schedule_day_of_week: null, schedule_day_of_month: null };
  return {
    schedule,
    schedule_day_of_week: schedule === 'weekly' ? dow || null : null,
    schedule_day_of_month: schedule === 'monthly' ? dom || null : null,
  };
}

function scheduleIsValid(schedule, dow, dom) {
  if (schedule === 'weekly') return Boolean(dow);
  if (schedule === 'monthly') return Boolean(dom);
  return true;
}

const SegmentEditor = () => {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const segmentsListPath = listReturnPath('/segments', location, SEGMENTS_LIST_PAGE_KEY);
  const seedFromId = isNew ? searchParams.get('from') : null;
  const seedTemplateId = isNew ? searchParams.get('template') : null;
  // Offer Reports v2 deep link: /segments/new?offer=<id>&rules=<encoded>&match=ALL|ANY
  const seedOfferId = isNew ? searchParams.get('offer') : null;
  const seedRules = isNew ? decodeRules(searchParams.get('rules')) : null;
  const seedHasRules = audienceRuleCount(seedRules);
  const seedMatch = searchParams.get('match') === 'ANY' ? 'OR' : 'AND';
  const { user } = useAuth();
  const { can } = usePermissions();
  const canCreateSegments = can(PERMISSIONS.SEGMENTS_CREATE);
  const canExport = can(PERMISSIONS.EXPORT);
  const isFastCubeAdmin = canManageTemplates(user);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const offerReportDraftApplied = useRef(false);

  const [organizations, setOrganizations] = useState([]);
  const [organizationId, setOrganizationId] = useState('');
  const [segment, setSegment] = useState(null); // saved server copy
  const [loading, setLoading] = useState(!isNew || Boolean(seedFromId || seedTemplateId || seedHasRules));
  const [loadError, setLoadError] = useState('');
  const [createError, setCreateError] = useState('');

  const [name, setName] = useState('');
  const [criteria, setCriteria] = useState(defaultCriteriaRoot());
  const [verticals, setVerticals] = useState([]);
  const [verticalsLoading, setVerticalsLoading] = useState(false);
  const [templateVertical, setTemplateVertical] = useState('');
  const [templateOfferLabel, setTemplateOfferLabel] = useState('');
  const [templateLoading, setTemplateLoading] = useState(false);
  // Vertical / offer picked in the template row but not applied yet: { type: 'vertical' | 'offer', ... }
  const [pendingTemplate, setPendingTemplate] = useState(null);
  const [confirmApplyOpen, setConfirmApplyOpen] = useState(false);
  const [schedule, setSchedule] = useState('');
  const [scheduleDayOfWeek, setScheduleDayOfWeek] = useState('');
  const [scheduleDayOfMonth, setScheduleDayOfMonth] = useState('');

  const [sql, setSql] = useState('');
  const [sqlError, setSqlError] = useState('');
  const [explain, setExplain] = useState(null);

  const [count, setCount] = useState(null);
  const [countStatus, setCountStatus] = useState('idle'); // idle | running | fresh | stale | error
  const [countTookMs, setCountTookMs] = useState(null);
  const [countError, setCountError] = useState('');
  const [autoCount, setAutoCount] = useState(true);
  // Unsampled size of every group from the last count, keyed by criteriaUtils pathKey.
  const [groupCounts, setGroupCounts] = useState(null);
  const [targetSize, setTargetSize] = useState('');

  const [analyzeStatus, setAnalyzeStatus] = useState('idle'); // idle | running | done | error
  const [analyzeResults, setAnalyzeResults] = useState(null); // [{ path, count, delta, error }]
  const [analyzeError, setAnalyzeError] = useState('');

  const [saveStatus, setSaveStatus] = useState('saved');
  const [creating, setCreating] = useState(false);
  const [exporting, setExporting] = useState(false);

  const criteriaJson = useMemo(() => JSON.stringify(criteria), [criteria]);
  const complete = useMemo(() => criteriaIsComplete(criteria), [criteria]);
  const treeIsEmpty = !(criteria?.children || []).length;
  const previewReq = useRef(0);
  const countReq = useRef(0);
  const analyzeReq = useRef(0);
  const templateReq = useRef(0);
  const countedFor = useRef(''); // criteriaJson the current count corresponds to
  const savedSnapshot = useRef(''); // JSON of last saved {name, criteriaJson, schedule...}
  const lastSqlFor = useRef('');
  const latestCriteriaJson = useRef('');
  latestCriteriaJson.current = criteriaJson;
  const saveInFlight = useRef(false);

  useEffect(() => {
    if (!isNew || offerReportDraftApplied.current) return;
    const draft = location.state?.offerReportDraft;
    if (!draft) return;
    offerReportDraftApplied.current = true;
    if (draft.name) setName(sanitizeName(draft.name));
    if (draft.criteria) setCriteria(criteriaFromSegment(draft.criteria));
    if (draft.templateVertical) setTemplateVertical(draft.templateVertical);
    if (draft.offer) setTemplateOfferLabel(offerLabel(draft.offer));
  }, [isNew, location.state]);

  const sortedOrganizations = useMemo(
    () => [...organizations].sort((a, b) => a.name.localeCompare(b.name)),
    [organizations]
  );
  const organizationName = useMemo(
    () => organizations.find((o) => String(o.id) === String(organizationId))?.name || segment?.organization_name || '',
    [organizations, organizationId, segment]
  );
  const isFastCubeOrg = organizationName === FASTCUBE_ORG;

  // ---- load org + segment -------------------------------------------------
  useEffect(() => {
    wakeSqlWarehouse();
    let cancelled = false;
    let loadedOrgs = [];
    (async () => {
      try {
        const orgs = await organizationsService.getOrganizations();
        if (cancelled) return;
        loadedOrgs = orgs;
        setOrganizations(orgs);
        if (isNew) {
          const saved = organizationsService.getSelectedOrganization('id');
          const pick = saved && orgs.some((o) => String(o.id) === String(saved)) ? String(saved) : orgs[0] ? String(orgs[0].id) : '';
          setOrganizationId(pick);
        }
      } catch (e) {
        if (!cancelled) setLoadError('Failed to load organizations.');
      }
      if (isNew) {
        if (seedFromId || seedTemplateId || seedHasRules) {
          try {
            let seedName = '';
            let seedGroupName = '';
            let seedCriteria = null;
            if (seedHasRules) {
              // Rules picked in the offer Audience Studio. Includes honor ALL/ANY;
              // every exclude is ANDed. Unmapped rules keep display_name + value
              // so the user only has to pick the column.
              seedCriteria = criteriaFromOfferSeed(seedRules, seedMatch);
              seedName = seedOfferId ? `Offer ${seedOfferId} — audience` : 'Offer audience';
              if (seedOfferId) {
                try {
                  const orgId = organizationsService.getSelectedOrganization('id') || loadedOrgs[0]?.id;
                  const ov = await api.get(`/api/v2/offer_reports/studio/${seedOfferId}/overview`, { params: { organization_id: orgId } });
                  if (ov.data?.offer?.offer_name) seedName = `${ov.data.offer.offer_name} — audience`;
                } catch (e) {
                  /* keep fallback name */
                }
              }
            } else if (seedFromId) {
              const res = await api.get(`/api/v2/segments/${seedFromId}`);
              seedName = `Copy of ${res.data?.name || ''}`.trim();
              seedCriteria = res.data?.criteria;
              if (res.data?.organization_id) setOrganizationId(String(res.data.organization_id));
            } else {
              const t = await segmentTemplatesService.get(seedTemplateId);
              seedName = t.name || '';
              seedCriteria = t.criteria;
              seedGroupName = t.name || '';
            }
            if (cancelled) return;
            const hydrated = await hydrateCriteria(
              criteriaFromSegment(seedCriteria),
              organizationsService.getSelectedOrganization('id') || loadedOrgs[0]?.id
            );
            if (cancelled) return;
            setName(sanitizeName(seedName));
            setCriteria(seedGroupName ? { ...hydrated, display_name: seedGroupName } : hydrated);
          } catch (e) {
            if (!cancelled) toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || 'Could not load the starting point');
          } finally {
            if (!cancelled) setLoading(false);
          }
        }
        return;
      }
      try {
        const res = await api.get(`/api/v2/segments/${id}`);
        if (cancelled) return;
        const seg = res.data;
        const hydrated = await hydrateCriteria(criteriaFromSegment(seg.criteria), seg.organization_id);
        if (cancelled) return;
        setSegment(seg);
        setOrganizationId(String(seg.organization_id));
        setName(seg.name || '');
        setCriteria(hydrated);
        setSchedule(seg.schedule || '');
        setScheduleDayOfWeek(seg.schedule_day_of_week || '');
        setScheduleDayOfMonth(seg.schedule_day_of_month || '');
        setSql(seg.sql || '');
        setCount(seg.md5_count ?? null);
        setCountStatus(seg.md5_count != null ? 'fresh' : 'idle');
        const cj = JSON.stringify(hydrated);
        countedFor.current = seg.md5_count != null ? cj : '';
        savedSnapshot.current = JSON.stringify({ name: seg.name || '', cj, s: seg.schedule || '', d: seg.schedule_day_of_week || '', m: seg.schedule_day_of_month || '' });
        setSaveStatus('saved');
      } catch (e) {
        if (!cancelled) setLoadError(formatApiErrorDetail(e.response?.data?.detail) || 'Segment not found.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew, seedFromId, seedTemplateId]);

  // ---- template verticals -------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setVerticalsLoading(true);
      try {
        const res = await api.get('/api/v2/verticals');
        if (cancelled) return;
        const rows = Array.isArray(res.data) ? res.data : [];
        setVerticals(
          [...rows].sort((a, b) =>
            String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
          )
        );
      } catch (e) {
        if (!cancelled) setVerticals([]);
      } finally {
        if (!cancelled) setVerticalsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** Replace the criteria tree with the top attributes for a vertical (or the General set). */
  const applyVerticalTemplate = async (templateKey) => {
    if (!templateKey) return;
    const req = ++templateReq.current;
    setTemplateLoading(true);
    try {
      const result = await buildCriteriaFromVertical(templateKey);
      if (req !== templateReq.current) return;
      const label = templateKey === GENERAL_TEMPLATE_VALUE ? 'General' : templateKey;
      if (result.applied === 0) {
        toast.error(
          result.total === 0
            ? `No top attributes found for ${label}.`
            : 'Could not match any attributes to catalog columns.'
        );
        return;
      }
      setCriteria(result.criteria);
      if (result.skipped.length) {
        toast.success(`Applied ${result.applied} of ${result.total} attributes. Skipped: ${result.skipped.join(', ')}`);
      } else {
        toast.success(`Applied ${result.applied} attributes from ${label}.`);
      }
    } catch (e) {
      if (req !== templateReq.current) return;
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed to apply template');
    } finally {
      if (req === templateReq.current) setTemplateLoading(false);
    }
  };

  const applyOfferTemplate = async (hit) => {
    if (!hit?.offer_id) return;
    if (!organizationId) {
      toast.error('Select an organization before applying an offer template.');
      return;
    }
    const req = ++templateReq.current;
    setTemplateLoading(true);
    try {
      const result = await buildCriteriaFromOffer(hit.offer_id, {
        organizationId: parseInt(organizationId, 10),
      });
      if (req !== templateReq.current) return;
      if (result.missing) {
        toast.error('No offer report found for this organization.');
        return;
      }
      if (result.applied === 0) {
        toast.error(
          result.total === 0
            ? 'No attributes available to apply from this offer.'
            : 'Could not match any attributes to catalog columns.'
        );
        return;
      }
      setCriteria(result.criteria);
      // Offer-specific reports (metrics / values) have no matching vertical entry.
      const offerHasOwnMetrics = result.offer?.has_metrics || result.offer?.has_values;
      if (offerHasOwnMetrics) {
        setTemplateVertical('');
      } else if (result.offer?.has_vertical) {
        setTemplateVertical(result.offer.category || '');
      } else {
        setTemplateVertical(GENERAL_TEMPLATE_VALUE);
      }
      setTemplateOfferLabel(offerLabel(hit));
      const label = hit.offer_name || `Offer ${hit.offer_id}`;
      if (result.skipped.length) {
        toast.success(`Applied ${result.applied} of ${result.total} attributes. Skipped: ${result.skipped.join(', ')}`);
      } else {
        toast.success(`Applied ${result.applied} attributes from ${label}.`);
      }
    } catch (e) {
      if (req !== templateReq.current) return;
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed to apply offer template');
    } finally {
      if (req === templateReq.current) setTemplateLoading(false);
    }
  };

  const applyPendingTemplate = () => {
    const pending = pendingTemplate;
    if (!pending) return;
    setPendingTemplate(null);
    if (pending.type === 'offer') applyOfferTemplate(pending.offer);
    else applyVerticalTemplate(pending.vertical);
  };

  const pendingTemplateLabel = !pendingTemplate
    ? ''
    : pendingTemplate.type === 'offer'
      ? `"${pendingTemplate.offer.offer_name || `Offer ${pendingTemplate.offer.offer_id}`}"`
      : `the ${pendingTemplate.vertical === GENERAL_TEMPLATE_VALUE ? 'General' : pendingTemplate.vertical} template`;

  // Applying replaces the whole criteria tree, so confirm first unless there is nothing to lose.
  const requestApplyTemplate = () => {
    if (!pendingTemplate || templateLoading) return;
    if (treeIsEmpty) applyPendingTemplate();
    else setConfirmApplyOpen(true);
  };

  // ---- preview SQL (debounced) --------------------------------------------
  useEffect(() => {
    if (!organizationId) return undefined;
    if (!complete) {
      previewReq.current += 1;
      setExplain(null);
      setSqlError('');
      return undefined;
    }
    if (criteriaJson === lastSqlFor.current) return undefined;
    const t = setTimeout(async () => {
      const req = ++previewReq.current;
      try {
        const res = await api.post('/api/v2/segments/preview-sql', { criteria, organization_id: parseInt(organizationId, 10) });
        if (req !== previewReq.current) return;
        setSql(res.data?.sql || '');
        setExplain(res.data?.explain || null);
        setSqlError('');
        lastSqlFor.current = criteriaJson;
      } catch (e) {
        if (req !== previewReq.current) return;
        setSqlError(formatApiErrorDetail(e.response?.data?.detail) || 'Could not generate SQL.');
        setExplain(null);
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criteriaJson, complete, organizationId]);

  // A count belongs to one exact criteria tree; mark it stale as soon as the tree differs.
  useEffect(() => {
    if (criteriaJson === countedFor.current) return;
    setCountError('');
    if (count != null) setCountStatus((s) => (s === 'running' ? s : 'stale'));
  }, [criteriaJson, count]);

  // ---- count --------------------------------------------------------------
  const runCount = useCallback(async () => {
    if (!organizationId || !criteriaIsComplete(criteria)) return;
    const cj = JSON.stringify(criteria);
    const req = ++countReq.current;
    setCountStatus('running');
    setCountError('');
    try {
      const res = await api.post('/api/v2/segments/run-count', {
        criteria,
        organization_id: parseInt(organizationId, 10),
        // Nested groups / sampling get per-group sizes from a breakdown scan;
        // a single root at 100% reuses the total as the slider estimate.
        include_group_counts: true,
      });
      if (req !== countReq.current) return;
      const n = Number(res.data?.count);
      setCount(Number.isFinite(n) ? n : null);
      setCountTookMs(Number(res.data?.took_ms) || null);
      const gc = res.data?.group_counts;
      setGroupCounts(gc && typeof gc === 'object' && Object.keys(gc).length ? gc : null);
      if (res.data?.sql) setSql(res.data.sql);
      countedFor.current = cj;
      // The tree may have moved on while Databricks was counting.
      setCountStatus(latestCriteriaJson.current === cj ? 'fresh' : 'stale');
    } catch (e) {
      if (req !== countReq.current) return;
      setCountStatus('error');
      setCountError(formatApiErrorDetail(e.response?.data?.detail) || 'Count failed.');
    }
  }, [criteria, organizationId]);

  useEffect(() => {
    if (!autoCount || !complete || !organizationId) return undefined;
    if (criteriaJson === countedFor.current) return undefined;
    const t = setTimeout(() => runCount(), COUNT_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [criteriaJson, complete, autoCount, organizationId, runCount]);

  // ---- analyze (leave-one-out condition impact) ----------------------------
  // Results belong to one exact criteria tree; drop them as soon as the tree changes.
  useEffect(() => {
    setAnalyzeStatus('idle');
    setAnalyzeResults(null);
    setAnalyzeError('');
  }, [criteriaJson]);

  const runAnalyze = useCallback(async () => {
    if (!organizationId || !criteriaIsComplete(criteria)) return;
    const req = ++analyzeReq.current;
    setAnalyzeStatus('running');
    setAnalyzeError('');
    try {
      const res = await api.post('/api/v2/segments/analyze-conditions', { criteria, organization_id: parseInt(organizationId, 10) });
      if (req !== analyzeReq.current) return;
      setAnalyzeResults({ baseCount: Number(res.data?.base_count) || 0, conditions: Array.isArray(res.data?.conditions) ? res.data.conditions : [] });
      setAnalyzeStatus('done');
    } catch (e) {
      if (req !== analyzeReq.current) return;
      setAnalyzeStatus('error');
      setAnalyzeError(formatApiErrorDetail(e.response?.data?.detail) || 'Analysis failed.');
    }
  }, [criteria, organizationId]);

  useEffect(() => {
    if (!organizationId) return;
    setAutoCount(readOrgFlag(organizationId, ORG_LOCAL_SUFFIX.segmentsAutoCount));
  }, [organizationId]);

  const toggleAutoCount = (v) => {
    setAutoCount(v);
    writeOrgLocal(organizationId, ORG_LOCAL_SUFFIX.segmentsAutoCount, String(v));
  };

  // ---- target audience size -----------------------------------------------
  const samplingActive = useMemo(() => anyGroupSampled(criteria), [criteria]);
  const matchingTotal = groupCounts ? groupCounts[pathKey([])] : null;

  // Fitting is an explicit request for a number, so confirm it with a count even
  // when auto-count is off. The flag is read once the new criteria have landed.
  const countAfterFit = useRef(false);

  /** Back-solve the percentage sliders so the segment lands on the target size. */
  const fitTargetSize = () => {
    const target = Number(String(targetSize).replace(/[^\d.]/g, ''));
    const result = fitToTarget(criteria, { groupCounts: groupCounts || {}, currentTotal: count, target });
    if (!result.ok) {
      toast.error(result.reason);
      return;
    }
    if (result.reason) toast.success(result.reason);
    setCriteria(result.criteria);
    countAfterFit.current = true;
  };

  useEffect(() => {
    if (!countAfterFit.current || !complete || !organizationId) return;
    countAfterFit.current = false;
    runCount();
  }, [criteriaJson, complete, organizationId, runCount]);

  // Groups taking a percentage inside an "All of" level, where percentages multiply
  // instead of adding, so the estimates and the target solver cannot be trusted.
  const shapeIssues = useMemo(() => samplingShapeIssues(criteria), [criteria]);

  /** Move each stranded percentage up to the level that combines the conditions. */
  const hoistStrandedSamples = () => {
    setCriteria((prev) => {
      let next = prev;
      // A hoist can land the percentage on a level that is itself inside an "All of",
      // so keep going until nothing is stranded. Two groups hoisting onto the same
      // level multiply, which is what they were already doing under AND.
      for (let pass = 0; pass < 8; pass += 1) {
        const issues = samplingShapeIssues(next);
        if (!issues.length) break;
        next = issues.reduce((acc, i) => hoistSampleToAncestor(acc, i.path, i.ancestorPath), next);
      }
      return next;
    });
    toast.success('Percentage moved up to the level above — the same profiles are still selected.');
    countAfterFit.current = true;
  };

  /** Switch each offending level to "Any of" so its groups contribute independent slices. */
  const loosenStrandedLevels = () => {
    setCriteria((prev) => {
      const levels = new Map(samplingShapeIssues(prev).map((i) => [pathKey(i.ancestorPath), i.ancestorPath]));
      return [...levels.values()].reduce((acc, p) => setOpAtPath(acc, p, 'OR'), prev);
    });
    toast.success('Switched to "Any of" — each group now adds its own slice, so the audience grows.');
    countAfterFit.current = true;
  };

  // Levels asking one column for several values at once, which no profile can satisfy.
  const contradictions = useMemo(() => contradictionIssues(criteria), [criteria]);

  /** Restate each contradictory column as one "is any of", leaving other columns alone. */
  const combineContradictions = () => {
    setCriteria((prev) =>
      contradictionIssues(prev)
        .filter((i) => i.canMerge)
        // Merging shortens a group's children, so later paths are read against the tree
        // as it stands at the time — deepest and last first keeps them valid.
        .reverse()
        .reduce((acc, i) => mergeSameColumnAtPath(acc, i.path, i.columnKey), prev)
    );
    toast.success('Combined into "is any of" — profiles matching any of those values now qualify.');
  };

  // ---- save ---------------------------------------------------------------
  const currentSnapshot = useMemo(
    () => JSON.stringify({ name: name.trim(), cj: criteriaJson, s: schedule, d: scheduleDayOfWeek, m: scheduleDayOfMonth }),
    [name, criteriaJson, schedule, scheduleDayOfWeek, scheduleDayOfMonth]
  );
  const dirty = !isNew && currentSnapshot !== savedSnapshot.current;
  const saveable = Boolean(name.trim()) && complete && scheduleIsValid(schedule, scheduleDayOfWeek, scheduleDayOfMonth);
  const notSaveableReason = !name.trim()
    ? 'Name the segment to save'
    : !complete
      ? criteriaIncompleteReason(criteria)
      : !scheduleIsValid(schedule, scheduleDayOfWeek, scheduleDayOfMonth)
        ? 'Pick a day for the schedule'
        : '';

  /** Everything standing between the current form and a saved segment, for the error dialog. */
  const createBlockers = () => {
    const blockers = [];
    if (!name.trim()) blockers.push('Give the segment a name.');
    if (!complete) blockers.push(criteriaIncompleteReason(criteria));
    if (!scheduleIsValid(schedule, scheduleDayOfWeek, scheduleDayOfMonth)) {
      blockers.push('Pick a day for the schedule.');
    }
    if (!organizationId) blockers.push('Select an organization.');
    return blockers.filter(Boolean);
  };

  const buildPayload = () => ({
    name: name.trim(),
    criteria,
    md5_count: countedFor.current === criteriaJson ? count : null,
    ...schedulePayload(schedule, scheduleDayOfWeek, scheduleDayOfMonth),
  });

  /** Save the current form to the existing segment. Resolves true on success. */
  const saveExisting = useCallback(async () => {
    // Viewers without segments.create can open the editor but nothing is written back.
    if (isNew || !segment || saveInFlight.current || !canCreateSegments) return false;
    const snap = currentSnapshot;
    saveInFlight.current = true;
    setSaveStatus('saving');
    try {
      const res = await api.put(`/api/v2/segments/${segment.id}`, buildPayload());
      setSegment((prev) => ({ ...prev, ...res.data }));
      savedSnapshot.current = snap;
      setSaveStatus('saved');
      return true;
    } catch (e) {
      setSaveStatus('error');
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed to save segment');
      return false;
    } finally {
      saveInFlight.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, segment, currentSnapshot, criteria, count, name, schedule, scheduleDayOfWeek, scheduleDayOfMonth, canCreateSegments]);

  useEffect(() => {
    if (isNew || !segment) return undefined;
    if (!dirty) {
      setSaveStatus((s) => (s === 'saving' ? s : 'saved'));
      return undefined;
    }
    if (!saveable) {
      setSaveStatus('invalid');
      return undefined;
    }
    setSaveStatus('dirty');
    const t = setTimeout(() => saveExisting(), SAVE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [dirty, saveable, isNew, segment, saveExisting, currentSnapshot]);

  // Flush a pending autosave if the user navigates away, and warn on tab close while dirty.
  const pendingSaveRef = useRef(null);
  pendingSaveRef.current = dirty && saveable && !saveInFlight.current ? saveExisting : null;
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (pendingSaveRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      if (pendingSaveRef.current) pendingSaveRef.current();
    };
  }, []);

  // Persist a fresh count on an already-saved segment so the list shows it (no other field changed).
  const lastPersistedCountFor = useRef('');
  useEffect(() => {
    if (isNew || !segment || countStatus !== 'fresh' || count == null) return;
    if (countedFor.current !== criteriaJson || dirty) return;
    if (lastPersistedCountFor.current === criteriaJson && segment.md5_count === count) return;
    lastPersistedCountFor.current = criteriaJson;
    if (segment.md5_count === count) return;
    saveExisting();
  }, [countStatus, count, criteriaJson, dirty, isNew, segment, saveExisting]);

  const createSegment = async () => {
    if (creating) return null;
    const blockers = createBlockers();
    if (blockers.length) {
      setCreateError(blockers.join('\n'));
      return null;
    }
    setCreating(true);
    try {
      const res = await api.post('/api/v2/segments/', { ...buildPayload(), organization_id: parseInt(organizationId, 10) });
      const seg = res.data;
      toast.success('Segment created');
      navigate(`/segments/${seg.id}`, { replace: true, state: location.state });
      return seg;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed to create segment');
      return null;
    } finally {
      setCreating(false);
    }
  };

  // ---- export -------------------------------------------------------------
  const refreshSegment = useCallback(async () => {
    if (!segment?.id) return;
    try {
      const res = await api.get(`/api/v2/segments/${segment.id}`);
      setSegment((prev) => ({ ...prev, ...res.data }));
    } catch (e) {
      /* ignore transient */
    }
  }, [segment?.id]);

  const runLower = (segment?.processing_status || '').toLowerCase();
  const inFlight = runLower === 'pending' || runLower === 'started' || runLower === 'processing';
  useEffect(() => {
    if (!inFlight) return undefined;
    const t = setInterval(refreshSegment, RUN_POLL_MS);
    return () => clearInterval(t);
  }, [inFlight, refreshSegment]);

  const exportSegment = async () => {
    if (!segment?.id) return;
    if (dirty || saveInFlight.current) {
      const ok = await saveExisting();
      if (!ok) {
        toast.error('Save the segment before creating a file.');
        return;
      }
    }
    setExporting(true);
    try {
      const res = await api.post(`/api/v2/segments/${segment.id}/segment_download_request`);
      const executionId = res.data?.execution_id;
      setSegment((prev) => ({ ...prev, execution_id: executionId || prev.execution_id, processing_status: 'pending' }));
      toast.success('Building your file — this usually takes a few minutes.');
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed to start export');
    } finally {
      setExporting(false);
    }
  };

  const downloadFile = () => {
    if (!segment?.id || !organizationName) {
      toast.error('Cannot download: missing organization or segment ID');
      return;
    }
    const href = buildFileDownloadHref(`/api/v2/segments/${segment.id}/requested_download`, {
      organization: organizationName,
    });
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const exportDisabledReason = isNew
    ? 'Create the segment first.'
    : !complete
      ? 'Finish your criteria first.'
      : sqlError
        ? 'Fix the criteria error first.'
        : '';

  const applySavedTemplate = async (t) => {
    setTemplatePickerOpen(false);
    const hydrated = await hydrateCriteria(criteriaFromSegment(t.criteria), organizationId);
    setCriteria({ ...hydrated, display_name: t.name || '' });
    if (!name.trim()) setName(sanitizeName(t.name || ''));
  };

  // ---- render -------------------------------------------------------------
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-20 text-sm text-zinc-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading segment…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1500px] space-y-5">
      {/* Header */}
      <div className="space-y-3">
        <Link to={segmentsListPath} className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-[#18181B]">
          <ArrowLeft className="h-4 w-4" /> Segments
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <input
            id="segment-name"
            type="text"
            value={name}
            onChange={(e) => setName(sanitizeName(e.target.value))}
            placeholder="Name your segment"
            aria-label="Segment name"
            className="h-11 min-w-[18rem] flex-1 rounded-lg border border-input bg-surface px-3 font-display text-xl font-bold text-[#18181B] placeholder:font-normal placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-[#18181B]/10"
          />
          <div className="flex items-center gap-3">
            {organizationName ? (
              <span className="rounded-full bg-[#E8EEF5] px-2.5 py-1 text-xs font-medium text-zinc-600" title="Organization">
                {organizationName}
              </span>
            ) : null}
            <SaveStatus status={saveStatus} isNew={isNew} invalidReason={notSaveableReason} />
            {organizationId && complete && canCreateSegments ? (
              <button
                type="button"
                onClick={() => setSaveTemplateOpen(true)}
                className="inline-flex h-10 items-center justify-center gap-1.5 rounded-md border border-hairline bg-surface px-4 py-2 font-medium text-[#18181B] hover:bg-[#E8EEF5]"
                title="Save these criteria as a reusable template"
              >
                <LayoutTemplate className="h-5 w-5 text-zinc-500" />
                Save as template
              </button>
            ) : null}
            {isNew && canCreateSegments ? (
              <Button variant="outline" size="icon" onClick={createSegment} aria-label="Create segment" title="Create segment">
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-sm text-zinc-600">
        <div className="w-72">
          <FunctionalSelect
            label="Vertical"
            value={templateVertical}
            onValueChange={(v) => {
              const next = v || '';
              setTemplateVertical(next);
              setPendingTemplate(next ? { type: 'vertical', vertical: next } : null);
            }}
            placeholder={
              verticalsLoading ? 'Loading verticals…' : templateLoading ? 'Applying template…' : 'Select vertical'
            }
            disabled={verticalsLoading || templateLoading}
          >
            <SelectItem value={GENERAL_TEMPLATE_VALUE}>General</SelectItem>
            {verticals.map((v) => (
              <SelectItem key={v.id} value={v.name}>
                {v.name}
              </SelectItem>
            ))}
          </FunctionalSelect>
        </div>
        <div className="w-80">
          <OfferSearchTypeahead
            placeholder="Offer name or ID"
            ariaLabel="Search offers by name or ID"
            disabled={templateLoading || !organizationId}
            resetKey={organizationId}
            organizationId={organizationId}
            selectedLabel={templateOfferLabel}
            onSelect={(hit) => setPendingTemplate(hit?.offer_id ? { type: 'offer', offer: hit } : null)}
            onClear={() => {
              setTemplateOfferLabel('');
              setPendingTemplate((prev) => (prev?.type === 'offer' ? null : prev));
            }}
          />
        </div>
        <Button
          variant="outline"
          onClick={requestApplyTemplate}
          disabled={!pendingTemplate || templateLoading}
          className="gap-1.5"
          title={pendingTemplate ? 'Replace the criteria with this template' : 'Pick a vertical or offer first'}
        >
          {templateLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Apply
        </Button>
      </div>

      {isNew && organizations.length > 1 ? (
        <PageHeaderActions>
          <FunctionalSelect
            label="Organization"
            className={HEADER_SELECT_CLASS}
            value={organizationsService.getSelectedOptionValue(sortedOrganizations, organizationId, 'id')}
            onValueChange={(value) => {
              const parsed = organizationsService.parseOrganizationOptionValue(value);
              const nextId = parsed ? parsed.id : value;
              setOrganizationId(nextId);
              organizationsService.setSelectedOrganization(nextId, 'id', sortedOrganizations);
              countedFor.current = '';
              lastSqlFor.current = '';
              setCount(null);
              setCountStatus('idle');
              setGroupCounts(null);
            }}
            placeholder="Select organization"
          >
            {sortedOrganizations.map((org) => (
              <SelectItem key={org.id} value={organizationsService.orgToOptionValue(org)}>
                {org.name}
              </SelectItem>
            ))}
          </FunctionalSelect>
        </PageHeaderActions>
      ) : null}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_380px] xl:grid-cols-[minmax(0,1fr)_420px]">
        <div className="flex flex-col gap-4">
          <NlDraftBar
            className="order-2"
            collapsible
            organizationId={organizationId}
            criteria={criteria}
            hasConditions={!treeIsEmpty}
            onApply={(tree) => setCriteria(criteriaFromSegment(tree))}
          />
          <div className={`order-1 rounded-xl border border-hairline bg-surface shadow-card ${treeIsEmpty ? '' : 'p-5'}`}>
            <CriteriaTree
              criteria={criteria}
              onChange={setCriteria}
              groupCounts={groupCounts}
              countsStale={countStatus === 'stale'}
              organizationId={organizationId}
              onStartFromTemplate={() => setTemplatePickerOpen(true)}
            />
          </div>
        </div>
        <div className="lg:sticky lg:top-0 lg:self-start">
          <SegmentSummaryPane
            criteria={criteria}
            sql={complete ? sql : ''}
            sqlError={sqlError}
            explain={complete ? explain : null}
            organizationName={organizationName}
            showTechnicalDetails={isFastCubeOrg}
            showSql={isFastCubeAdmin}
            count={count}
            countStatus={countStatus}
            countTookMs={countTookMs}
            countError={countError}
            onRunCount={runCount}
            autoCount={autoCount}
            onToggleAutoCount={toggleAutoCount}
            canRunCount={complete && Boolean(organizationId)}
            analyzeStatus={analyzeStatus}
            analyzeResults={analyzeResults}
            analyzeError={analyzeError}
            onAnalyze={runAnalyze}
            conditionCount={collectConditions(criteria).length}
            samplingActive={samplingActive}
            matchingTotal={matchingTotal}
            contradictions={contradictions}
            onCombineContradictions={combineContradictions}
            targetSize={targetSize}
            onTargetSizeChange={setTargetSize}
            onFitTargetSize={fitTargetSize}
            canFitTargetSize={complete && matchingTotal != null}
            shapeIssues={shapeIssues}
            onHoistSample={hoistStrandedSamples}
            onLoosenLevel={loosenStrandedLevels}
            schedule={schedule}
            onScheduleChange={(v) => {
              setSchedule(v);
              if (v !== 'weekly') setScheduleDayOfWeek('');
              if (v !== 'monthly') setScheduleDayOfMonth('');
            }}
            scheduleDayOfWeek={scheduleDayOfWeek}
            onScheduleDayOfWeekChange={setScheduleDayOfWeek}
            scheduleDayOfMonth={scheduleDayOfMonth}
            onScheduleDayOfMonthChange={setScheduleDayOfMonth}
            segmentId={segment?.id}
            executionId={segment?.execution_id || ''}
            runStatus={segment?.processing_status || ''}
            exporting={exporting}
            onExport={exportSegment}
            onDownload={downloadFile}
            exportDisabledReason={
              canExport ? exportDisabledReason : 'Your role in this organization does not allow exports.'
            }
            canExport={canExport}
          />
        </div>
      </div>

      <TemplatePicker
        isOpen={templatePickerOpen}
        onClose={() => setTemplatePickerOpen(false)}
        onPick={applySavedTemplate}
        organizationId={organizationId}
        isFastCubeAdmin={isFastCubeAdmin}
      />
      <SaveTemplateDialog
        isOpen={saveTemplateOpen}
        onClose={() => setSaveTemplateOpen(false)}
        criteria={criteria}
        defaultName={name.trim() ? `${name.trim()} - Template` : ''}
        organizationId={organizationId}
        organizationName={organizationName}
      />
      <ConfirmDialog
        isOpen={confirmApplyOpen}
        onClose={() => setConfirmApplyOpen(false)}
        onConfirm={applyPendingTemplate}
        title="Replace your criteria?"
        message={`Applying ${pendingTemplateLabel || 'this template'} replaces every condition in the builder. Any changes you have made will be lost.`}
        confirmText="Apply template"
        cancelText="Cancel"
      />
      <ErrorDialog
        isOpen={Boolean(createError)}
        onClose={() => setCreateError('')}
        title="Cannot create segment"
        message={createError}
      />
      <ErrorDialog isOpen={Boolean(loadError)} onClose={() => navigate(segmentsListPath)} message={loadError} />
    </div>
  );
};

export default SegmentEditor;
